import { Ops } from '@willie/schemas'
import { readingValue, type ConfirmedReading, type ReadingSource } from '@willie/installation-guides'
import type { WillieSupabaseClient } from '@willie/supabase'
import {
  OPS_BUCKET,
  opsSignedUrlExpiresAt,
  parseStoredImageRef,
  signOpsPath,
  signOpsPaths
} from '@willie/supabase/storage'
import type { z } from 'zod'
import type { TRPCOutputs } from '~/utils/trpc'
import { todayIso } from '~/utils/date'
import {
  COMPATIBILITY_STATUS_OPTIONS,
  IMAGE_ACCEPT,
  STAGE_OPTIONS,
  type CompatibilityStatus,
  type Stage
} from './constants'
import { randomHex } from '~/utils/random'

const fileExtension = (name: string, fallback = 'bin'): string => {
  const extension = name.split('.').pop()
  // `pop()` returns the whole name when there's no dot — treat that as "no extension".
  return extension && extension !== name ? extension.toLowerCase() : fallback
}

export type InstallationWithRelations = NonNullable<TRPCOutputs['ops']['stationInstallations']['get']>

export const formSchema = Ops.StationInstallations.updatableFieldsSchema
export type FormValues = z.infer<typeof formSchema>

export const computeStage = (row: {
  pre_installation_image_added_at: string | null
  compatibility_status: CompatibilityStatus
  order_placed_date: string | null
  logger_shipping_date: string | null
  installation_date: string | null
  index_aligned_date: string | null
}): Stage => {
  if (row.index_aligned_date) return 'index_aligned'
  if (row.installation_date) return 'station_installed'
  if (row.logger_shipping_date) return 'hardware_shipped'
  if (row.order_placed_date) return 'order_placed'
  // Not a date: marking a profile supported completes the step without touching the installation.
  if (Ops.StationInstallations.isOrderableCompatibility(row.compatibility_status)) return 'compatibility_checked'
  if (row.pre_installation_image_added_at) return 'image_received'
  return 'install_created'
}

/** The latest of the verdict and the approval, once the status is orderable. */
export const compatibilityCheckedDate = (
  row: Pick<
    InstallationWithRelations,
    'compatibility_status' | 'compatibility_verdict_date' | 'unsupported_profile_approved_at'
  >
): string | null => {
  if (!Ops.StationInstallations.isOrderableCompatibility(row.compatibility_status)) return null
  const approvedOn = row.unsupported_profile_approved_at?.slice(0, 10) ?? null
  return (
    [row.compatibility_verdict_date, approvedOn]
      .filter((date): date is string => date !== null)
      .sort()
      .at(-1) ?? null
  )
}

export const needsCompatibilityCheck = (row: Parameters<typeof computeStage>[0]): boolean =>
  computeStage(row) === 'image_received' &&
  !Ops.StationInstallations.hasCompatibilityVerdict(row.compatibility_status)

// The assistant records a submission as an event and deliberately leaves installation_date unset:
// stamping it is ops' act of verifying, and so is what clears this.
export const needsVerification = (row: {
  submitted: { occurred_at: string } | null
  installation_date: string | null
}): boolean => row.submitted !== null && row.installation_date === null

export const needsInstructions = (
  row: Parameters<typeof computeStage>[0] & { instruction_sent_date: string | null }
): boolean => computeStage(row) === 'hardware_shipped' && row.instruction_sent_date === null

/** The lifecycle job only emails installations it can group by site and site contact. */
export const canReceiveInstructions = (row: {
  site_id: string | null
  site_contact: { email: string | null } | null
}): boolean => row.site_id !== null && !!row.site_contact?.email

export const isRetired = (row: { retired_at: string | null }): boolean => row.retired_at !== null

export const emptyByStage = (): Record<Stage, number> =>
  STAGE_OPTIONS.reduce(
    (acc, stage) => {
      acc[stage] = 0
      return acc
    },
    {} as Record<Stage, number>
  )

export const countByStage = (rows: Parameters<typeof computeStage>[0][]): Record<Stage, number> =>
  rows.reduce((acc, row) => {
    acc[computeStage(row)] += 1
    return acc
  }, emptyByStage())

const emptyByCompatibility = (): Record<CompatibilityStatus, number> =>
  COMPATIBILITY_STATUS_OPTIONS.reduce(
    (acc, status) => {
      acc[status] = 0
      return acc
    },
    {} as Record<CompatibilityStatus, number>
  )

export const countByCompatibility = (
  rows: { compatibility_status: CompatibilityStatus }[]
): Record<CompatibilityStatus, number> =>
  rows.reduce((acc, row) => {
    acc[row.compatibility_status] += 1
    return acc
  }, emptyByCompatibility())

export const toFormValues = (row: InstallationWithRelations): FormValues => ({
  name: row.name,
  internalId: row.internal_id,
  siteContactId: row.site_contact_id,
  installationContactId: row.installation_contact_id,
  loggerSerialNumber: row.logger_serial_number,
  pulseWeight: row.pulse_weight,
  preInstallationMonitoring: row.pre_installation_monitoring as FormValues['preInstallationMonitoring'],
  orderPlacedDate: row.order_placed_date,
  instructionSentDate: row.instruction_sent_date,
  loggerShippingDate: row.logger_shipping_date,
  installationDate: row.installation_date,
  plannedInstallationDate: row.planned_installation_date,
  indexAlignedDate: row.index_aligned_date,
  installationReminderDate: row.installation_reminder_date,
  preInstallationImageUrl: row.pre_installation_image_url,
  preInstallationImageAddedAt: row.pre_installation_image_added_at
})

export const uploadPreInstallationImage = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number,
  file: File
): Promise<string> => {
  const extension = fileExtension(file.name)
  const path = `installations/${installationId}/pre-install.${extension}`
  const { error } = await supabaseClient.storage.from(OPS_BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: true,
    cacheControl: '10'
  })
  if (error) throw error
  // Returns the storage path, not a URL: the bucket is private, and a signed URL
  // would expire long before the row outlives it. Resolve on read instead.
  return path
}

export const deletePreInstallationImage = async (
  supabaseClient: WillieSupabaseClient,
  path: string
): Promise<void> => {
  const { error } = await supabaseClient.storage.from(OPS_BUCKET).remove([path])
  if (error) throw error
}

const INSTALLATION_PHOTOS_DIR = 'photos'

const PHOTO_EXTENSIONS = Object.values(IMAGE_ACCEPT)
  .flat()
  .map((ext) => ext.replace(/^\./, ''))

export interface InstallationPhoto {
  name: string
  /** Storage key, kept so a share link can be re-signed at a longer lifetime. */
  path: string
  /** Signed for display; null when this object alone failed to sign. */
  url: string | null
  createdAt: string
  size: number
}

const photosDir = (installationId: number): string => `installations/${installationId}/${INSTALLATION_PHOTOS_DIR}`

const hasPhotoExtension = (name: string): boolean => PHOTO_EXTENSIONS.includes(fileExtension(name, ''))

// Dropzone accepts on MIME alone, so the name may lack a usable extension; fall back to
// the MIME's canonical extension so the stored name always passes hasPhotoExtension.
const photoExtension = (file: File): string => {
  const nameExtension = fileExtension(file.name, '')
  if (PHOTO_EXTENSIONS.includes(nameExtension)) return nameExtension
  return IMAGE_ACCEPT[file.type]?.[0]?.replace(/^\./, '') ?? 'bin'
}

export const generateInstallationPhotoFileName = (file: File): string => {
  const datePart = todayIso().replace(/-/g, '')
  return `${datePart}-${randomHex(4)}.${photoExtension(file)}`
}

export const listInstallationPhotos = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number
): Promise<InstallationPhoto[]> => {
  const dir = photosDir(installationId)
  const { data, error } = await supabaseClient.storage
    .from(OPS_BUCKET)
    .list(dir, { sortBy: { column: 'created_at', order: 'asc' } })
  if (error) throw error

  const entries = (data ?? []).filter((entry) => hasPhotoExtension(entry.name))
  const paths = entries.map((entry) => `${dir}/${entry.name}`)
  const signed = await signOpsPaths(supabaseClient, paths, 'display')

  return entries.map((entry, index) => {
    const path = paths[index] ?? `${dir}/${entry.name}`
    return {
      name: entry.name,
      path,
      url: signed[path] ?? null,
      createdAt: entry.created_at,
      size: entry.metadata?.size ?? 0
    }
  })
}

export const uploadInstallationPhoto = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number,
  file: File
): Promise<InstallationPhoto> => {
  const fileName = generateInstallationPhotoFileName(file)
  const path = `${photosDir(installationId)}/${fileName}`
  const { error } = await supabaseClient.storage.from(OPS_BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false
  })
  if (error) throw error
  const url = await signOpsPath(supabaseClient, path, 'display')
  return { name: fileName, path, url, createdAt: new Date().toISOString(), size: file.size }
}

export interface ShareableImageLink {
  url: string
  expiresAt: Date
}

/**
 * Mints a link an operator can send to a customer. Signed URLs cannot be withdrawn
 * before they expire, so the lifetime is the only control — hence a fixed audience
 * rather than a caller-chosen duration.
 */
export const createShareableImageLink = async (
  supabaseClient: WillieSupabaseClient,
  path: string
): Promise<ShareableImageLink> => ({
  url: await signOpsPath(supabaseClient, path, 'clientShare'),
  expiresAt: opsSignedUrlExpiresAt('clientShare')
})

/**
 * Signs the images an order CSV will carry, keyed by the stored value so an export
 * column can look one up by whatever the row holds.
 *
 * Third-party-hosted images are absent from the map on purpose: they resolve on their
 * own and we hold no credential to sign them. So is anything that failed to sign —
 * the export treats a missing key as a blocking gap rather than shipping an
 * unusable path to the provider.
 */
export const signImageRefsForProviderOrder = async (
  supabaseClient: WillieSupabaseClient,
  storedRefs: (string | null | undefined)[]
): Promise<Record<string, string>> => {
  const ours = storedRefs
    .filter((ref): ref is string => !!ref)
    .map((stored) => ({ stored, ref: parseStoredImageRef(stored) }))
    .flatMap((entry) => (entry.ref.kind === 'opsPath' ? [{ stored: entry.stored, path: entry.ref.path }] : []))

  const signed = await signOpsPaths(supabaseClient, [...new Set(ours.map((entry) => entry.path))], 'providerOrder')

  return ours.reduce<Record<string, string>>((acc, entry) => {
    const url = signed[entry.path]
    return url ? { ...acc, [entry.stored]: url } : acc
  }, {})
}

export const ATTACHMENT_KINDS = Ops.InstallationAttachments.captureKindSchema.options
export type AttachmentKind = Ops.InstallationAttachments.CaptureKind

export interface InstallationCapture {
  id: string
  kind: AttachmentKind
  stepKey: string | null
  path: string
  /** Signed for display; null when this object alone failed to sign. */
  url: string | null
  /** Server clock — the trusted one. */
  receivedAt: string
  /** Device clock, absent when the browser gave us nothing and wrong whenever the phone was. */
  capturedAt: string | null
  submittedBy: string | null
  /** How many times this slot was shot before the one shown. */
  retakeCount: number
}

export const CAPTURE_VERDICTS = ['pass', 'retake', 'confirm', 'unavailable'] as const

export type CaptureVerdict = (typeof CAPTURE_VERDICTS)[number]

/** One gate evaluation. Not one per photo: a gate is re-run over photos already on file. */
export interface CaptureGateVerdict {
  id: string
  attachmentId: string
  verdict: CaptureVerdict
  /** Set only on `unavailable` — why no verdict was reached. */
  unavailableReason: string | null
  /** Which evaluation this was for the photo. A re-run continues the photo's own sequence. */
  attempt: number
  /** Every declared assertion as the model answered it. */
  assertions: { id: string; met: boolean }[]
  /** Digits and how many are decimals, unconfirmed. `{ raw: '0624919', decimalDigits: 1 }` is 062491,9. */
  readings: { id: string; raw: string | null; decimalDigits: number | null }[]
  instruction: string | null
  /** What the installer said the register reads, beside `readings` rather than over it. */
  confirmedReadings: ConfirmedReading[]
  confirmedAt: string | null
  /** Enforcement was on for this evaluation, decided server-side from a PostHog flag. */
  enforced: boolean
  promptVersion: string
  createdAt: string
}

interface CaptureVerdictRow {
  id: string
  attachment_id: string
  verdict: CaptureVerdict
  unavailable_reason: string | null
  attempt: number
  assertions: unknown
  readings: unknown
  instruction: string | null
  confirmed_readings: unknown
  confirmed_at: string | null
  enforced: boolean
  prompt_version: string
  created_at: string
}

/**
 * The newest gate verdict per photo, keyed by attachment id.
 *
 * Newest rather than all of them for the same reason `listInstallationCaptures` shows the newest
 * capture: a re-run after a prompt change leaves the older evaluations behind as history, and the
 * question an operator has is what the gate says now.
 */
export const listCaptureVerdicts = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number
): Promise<Map<string, CaptureGateVerdict>> => {
  const { data, error } = await supabaseClient
    .from('installation_capture_verdicts')
    .select(
      'id, attachment_id, verdict, unavailable_reason, attempt, assertions, readings, instruction, confirmed_readings, confirmed_at, enforced, prompt_version, created_at'
    )
    .eq('installation_id', installationId)
    .order('created_at', { ascending: false })
  if (error) throw error

  return ((data ?? []) as CaptureVerdictRow[]).reduce((newest, row) => {
    // Ordered newest-first, so the first row seen for a photo is the current verdict.
    if (newest.has(row.attachment_id)) return newest
    return newest.set(row.attachment_id, {
      id: row.id,
      attachmentId: row.attachment_id,
      verdict: row.verdict,
      unavailableReason: row.unavailable_reason,
      attempt: row.attempt,
      assertions: Array.isArray(row.assertions) ? (row.assertions as { id: string; met: boolean }[]) : [],
      readings: Array.isArray(row.readings)
        ? (row.readings as { id: string; raw: string | null; decimalDigits: number | null }[])
        : [],
      instruction: row.instruction,
      confirmedReadings: Array.isArray(row.confirmed_readings)
        ? (row.confirmed_readings as ConfirmedReading[])
        : [],
      confirmedAt: row.confirmed_at,
      enforced: row.enforced,
      promptVersion: row.prompt_version,
      createdAt: row.created_at
    })
  }, new Map<string, CaptureGateVerdict>())
}

/** One confirmed index on this installation, for the install form's prefill. */
export interface ConfirmedIndex {
  value: number
  source: ReadingSource
  confirmedAt: string
  /** Null for the install-time capture; the verification round for one taken after go-live. */
  round: number | null
}

/**
 * Every confirmed index on this installation, newest first, each labelled with the round it belongs to.
 *
 * Read on its own rather than off `listCaptureVerdicts`. That function reduces to the newest verdict
 * per photo, so a prompt re-run would hide a confirmation behind a newer unconfirmed row — and these
 * values are what an operator is shown in place of typing an index by hand.
 */
export const listConfirmedIndexes = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number
): Promise<ConfirmedIndex[]> => {
  const { data, error } = await supabaseClient
    .from('installation_capture_verdicts')
    .select('confirmed_readings, confirmed_at, installation_index_captures(round)')
    .eq('installation_id', installationId)
    .not('confirmed_at', 'is', null)
    .order('confirmed_at', { ascending: false })
  if (error) throw error

  return (data ?? []).flatMap((row) => {
    const readings = Array.isArray(row.confirmed_readings) ? (row.confirmed_readings as ConfirmedReading[]) : []
    const index = readings[0]
    if (!index || row.confirmed_at === null) return []

    return [
      {
        value: readingValue(index),
        source: index.source,
        confirmedAt: row.confirmed_at,
        round: row.installation_index_captures[0]?.round ?? null
      }
    ]
  })
}

/**
 * The newest capture per kind, with the retakes counted rather than listed.
 *
 * Retakes insert rather than replace — reshooting a blurry index is the normal case — so the read
 * has to reduce to the latest per (kind, step_key). Doing it client-side rather than with the
 * `distinct on` the migration documents, because PostgREST cannot express it and the row count per
 * installation is small.
 */
export const listInstallationCaptures = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number
): Promise<InstallationCapture[]> => {
  const { data, error } = await supabaseClient
    .from('installation_attachments')
    .select('id, kind, step_key, storage_path, received_at, captured_at, created_by_email')
    .eq('installation_id', installationId)
    .in('kind', ATTACHMENT_KINDS)
    .order('received_at', { ascending: false })
  if (error) throw error

  const rows = data ?? []
  const newestPerSlot = rows.reduce<Map<string, (typeof rows)[number]>>((acc, row) => {
    const slot = `${row.kind}:${row.step_key ?? ''}`
    // Ordered newest-first, so the first row seen for a slot is the one to show.
    if (!acc.has(slot)) acc.set(slot, row)
    return acc
  }, new Map())

  const latest = [...newestPerSlot.values()]
  const signed = await signOpsPaths(
    supabaseClient,
    latest.map((row) => row.storage_path),
    'display'
  )

  return latest
    .map((row) => ({
      id: row.id,
      kind: row.kind as AttachmentKind,
      stepKey: row.step_key,
      path: row.storage_path,
      url: signed[row.storage_path] ?? null,
      receivedAt: row.received_at,
      capturedAt: row.captured_at,
      submittedBy: row.created_by_email,
      retakeCount:
        rows.filter((other) => `${other.kind}:${other.step_key ?? ''}` === `${row.kind}:${row.step_key ?? ''}`)
          .length - 1
    }))
    .sort((left, right) => ATTACHMENT_KINDS.indexOf(left.kind) - ATTACHMENT_KINDS.indexOf(right.kind))
}

export interface SubmittedIndexReading {
  value: number
  readingAt: string
}

/**
 * The most recent index reading for the installation's station, so an operator can eyeball it
 * against the meter-dial photo without opening the image — which is the whole point of collecting
 * both. Returns null when the installation has no station linked yet.
 */
export const getSubmittedIndexReading = async (
  supabaseClient: WillieSupabaseClient,
  waterStationId: string | null
): Promise<SubmittedIndexReading | null> => {
  if (!waterStationId) return null

  const { data, error } = await supabaseClient
    .from('station_index_readings')
    .select('value, reading_at')
    .eq('station_id', waterStationId)
    .order('reading_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error

  return data ? { value: data.value, readingAt: data.reading_at } : null
}

export const deleteInstallationPhoto = async (
  supabaseClient: WillieSupabaseClient,
  installationId: number,
  fileName: string
): Promise<void> => {
  const { error } = await supabaseClient.storage
    .from(OPS_BUCKET)
    .remove([`${photosDir(installationId)}/${fileName}`])
  if (error) throw error
}
